import { useMemo, useCallback } from 'react';
import { gql, useMutation } from '@apollo/client';

import {
  FontLicenseType,
  AddFontFacesToCurrentOrderMutation,
  AddFontFacesToCurrentOrderMutationVariables,
  RemoveFontFacesFromCurrentOrderMutation,
  RemoveFontFacesFromCurrentOrderMutationVariables,
} from 'client/graphql/types/operations';

import useLanguage from 'utils/useLanguage';
import { priceForFace, isComplementaryFaceSelected } from './pricing';

import useCurrentOrderQuery, {
  CURRENT_ORDER_QUERY,
  CURRENT_ORDER_FRAGMENT,
  CurrentOrderQuery,
} from 'queries/useCurrentOrderQuery';

const ADD_FONT_FACES_TO_CURRENT_ORDER_MUTATION = gql`
  mutation AddFontFacesToCurrentOrderMutation(
    $input: AddFontFacesToCurrentOrderInput!
    $language: Language!
  ) {
    addFontFacesToCurrentOrder(input: $input) {
      order {
        ...CurrentOrderFragment
      }

      errors {
        attribute
        message
      }
    }
  }

  ${CURRENT_ORDER_FRAGMENT}
`;

const REMOVE_FONT_FACES_FROM_CURRENT_ORDER_MUTATION = gql`
  mutation RemoveFontFacesFromCurrentOrderMutation(
    $input: RemoveFontFacesFromCurrentOrderInput!
    $language: Language!
  ) {
    removeFontFacesFromCurrentOrder(input: $input) {
      order {
        ...CurrentOrderFragment
      }

      errors {
        attribute
        message
      }
    }
  }

  ${CURRENT_ORDER_FRAGMENT}
`;

interface Options {
  licenseOption: {
    id: number;
    type: FontLicenseType;
    multiplier: number;
  } | null;
  family: {
    id: number;
    completePrice: number;
    subPackageFontFacePrice: number;
    faces: { id: number }[];
  };
  faces: {
    id: number;
    width: { slug: string; name: string; position: number };
    size: { slug: string; name: string; position: number };
    weight: { slug: string; name: string; position: number };
    family: { fontFacePrice: number; discountedFontFacePrice: number };
  }[];
}

const useCart = (options: Options) => {
  const { licenseOption, family, faces } = options;

  const language = useLanguage();

  const { data, loading } = useCurrentOrderQuery();

  const [addFontFacesToCurrentOrder, { loading: addingFontFaces }] =
    useMutation<
      AddFontFacesToCurrentOrderMutation,
      AddFontFacesToCurrentOrderMutationVariables
    >(ADD_FONT_FACES_TO_CURRENT_ORDER_MUTATION, {
      update: (cache, result) => {
        if (result.data?.addFontFacesToCurrentOrder?.order != null) {
          cache.writeQuery<CurrentOrderQuery>({
            query: CURRENT_ORDER_QUERY,
            data: {
              __typename: 'Query',
              currentOrder: result.data.addFontFacesToCurrentOrder.order,
            },
          });
        }
      },
    });

  const [removeFontFacesFromCurrentOrder, { loading: removingFontFaces }] =
    useMutation<
      RemoveFontFacesFromCurrentOrderMutation,
      RemoveFontFacesFromCurrentOrderMutationVariables
    >(REMOVE_FONT_FACES_FROM_CURRENT_ORDER_MUTATION);

  const license = useMemo(() => {
    if (data?.currentOrder == null) {
      return null;
    }

    return data.currentOrder.fontLicenses.find(
      (license) =>
        license.family.id === family.id &&
        license.licenseOption.id === licenseOption?.id,
    );
  }, [data, family, licenseOption]);

  const facesInOrder = useMemo(() => {
    if (license == null) {
      return [];
    }

    return faces.filter((face) =>
      license.faces.some((licenseFace) => licenseFace.id === face.id),
    );
  }, [faces, license]);

  const allFacesInOrder = faces.length === facesInOrder.length;

  const price = useMemo(() => {
    if (faces.length === 1) {
      return priceForFace(faces[0], license?.faces ?? []);
    } else if (faces.length === family.faces.length) {
      switch (licenseOption?.type) {
        case FontLicenseType.Student:
          return window.ENV.studentLicensePrice;
        case FontLicenseType.Concept:
          return 0;
        default:
          return family.completePrice;
      }
    } else {
      return family.subPackageFontFacePrice * faces.length;
    }
  }, [
    faces,
    license?.faces,
    family.completePrice,
    family.faces.length,
    family.subPackageFontFacePrice,
    licenseOption?.type,
  ]);

  const priceOfIndividualStylesInCart = useMemo(() => {
    if (faces.length === 1) {
      return 0;
    } else if (faces.length === family.faces.length) {
      return license != null ? license.total : 0;
    } else {
      return (
        facesInOrder.reduce(
          (total, face) => total + priceForFace(face, license?.faces ?? []),
          0,
        ) * (licenseOption?.multiplier ?? 0)
      );
    }
  }, [
    faces,
    license,
    facesInOrder,
    family.faces.length,
    licenseOption?.multiplier,
  ]);

  const addFaces = useCallback(() => {
    if (licenseOption == null) {
      return;
    }

    addFontFacesToCurrentOrder({
      variables: {
        input: {
          licenseOptionId: licenseOption.id,
          familyId: family.id,
          faceIds: faces.map(({ id }) => id),
        },
        language,
      },
    });
  }, [addFontFacesToCurrentOrder, licenseOption, family, faces, language]);

  const removeFaces = useCallback(() => {
    if (licenseOption == null) {
      return;
    }

    const faceIds = faces.map(({ id }) => id);

    removeFontFacesFromCurrentOrder({
      variables: {
        input: {
          licenseOptionId: licenseOption.id,
          familyId: family.id,
          faceIds,
        },
        language,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        removeFontFacesFromCurrentOrder: {
          __typename: 'RemoveFontFacesFromCurrentOrderPayload',
          order:
            data?.currentOrder != null
              ? {
                  ...data.currentOrder,
                  fontLicenses: data.currentOrder.fontLicenses.map(
                    (license) => {
                      if (
                        license.licenseOption.id !== licenseOption.id ||
                        license.family.id !== family.id
                      ) {
                        return license;
                      }

                      return {
                        ...license,
                        faces: license.faces.filter(
                          (face) => !faceIds.includes(face.id),
                        ),
                      };
                    },
                  ),
                }
              : null,
          errors: [],
        },
      },
    });
  }, [
    language,
    removeFontFacesFromCurrentOrder,
    licenseOption,
    family,
    faces,
    data,
  ]);

  const toggleFaces = useCallback(() => {
    if (!allFacesInOrder) {
      addFaces();
    } else {
      removeFaces();
    }
  }, [allFacesInOrder, addFaces, removeFaces]);

  const complementaryFaceSelected = useMemo(() => {
    if (faces.length > 1) {
      return false;
    }

    return isComplementaryFaceSelected(faces[0], license?.faces ?? []);
  }, [faces, license?.faces]);

  return {
    loading: loading || addingFontFaces || removingFontFaces,
    allFacesInOrder,
    complementaryFaceSelected,
    priceOfIndividualStylesInCart,
    totalPrice: price * (licenseOption?.multiplier ?? 0),
    addFaces,
    removeFaces,
    toggleFaces,
  };
};

export default useCart;
